import AcxMainTextField from "components/UI/AcxMainTextField";
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
} from "react";

export const MIN_TEMPERATURE = 0;
export const MAX_TEMPERATURE = 2;

type RegisterTemperatureValidity = (id: string, invalid: boolean) => void;

/**
 * Lets deeply-nested TemperatureFields report out-of-range input up to the
 * settings dialog/page so it can disable its save/apply action.
 */
export const TemperatureValidityContext =
    createContext<RegisterTemperatureValidity | null>(null);

/**
 * Tracks which temperature fields are currently out of range. Provide
 * `registerTemperatureValidity` via TemperatureValidityContext and use
 * `hasInvalidTemperature` to disable the save/apply button.
 */
export function useTemperatureValidity() {
    const [invalidIds, setInvalidIds] = useState<Set<string>>(new Set());
    const registerTemperatureValidity =
        useCallback<RegisterTemperatureValidity>((id, invalid) => {
            setInvalidIds((prev) => {
                if (invalid === prev.has(id)) return prev;
                const next = new Set(prev);
                if (invalid) next.add(id);
                else next.delete(id);
                return next;
            });
        }, []);
    return {
        hasInvalidTemperature: invalidIds.size > 0,
        registerTemperatureValidity,
    };
}

interface Props {
    id: string;
    /** Committed override value, or null when inheriting. */
    value: number | null;
    /** Inherited/default value, shown as placeholder when value is null. */
    placeholder?: number | null;
    disabled?: boolean;
    /** Fires with a clamped-valid number, or null when cleared. Does not fire
     *  while the typed value is out of range -- the error is surfaced instead. */
    onChange: (value: number | null) => void;
}

/**
 * Temperature input that validates the 0-2 range the backend enforces.
 * Keeps a local string so partial decimals ("0." / "1.5") can be typed, shows a
 * visible error when out of range, and only commits valid values upstream.
 */
export function TemperatureField({
    id,
    value,
    placeholder,
    disabled,
    onChange,
}: Props) {
    const [text, setText] = useState(value != null ? String(value) : "");

    // Re-sync when the committed value changes from outside (reset, inherit,
    // load) without clobbering in-progress local typing like "0." or "1.5".
    useEffect(() => {
        const parsed = parseFloat(text);
        const matchesLocal =
            value != null && Number.isFinite(parsed) && parsed === value;
        if (matchesLocal) return;
        if (value == null && text === "") return;
        setText(value != null ? String(value) : "");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const num = parseFloat(text);
    const outOfRange =
        text.trim() !== "" &&
        (!Number.isFinite(num) ||
            num < MIN_TEMPERATURE ||
            num > MAX_TEMPERATURE);

    // Report validity to the surrounding settings dialog/page so it can block
    // saving while this field is out of range. Clear on unmount (e.g. tab switch).
    const register = useContext(TemperatureValidityContext);
    useEffect(() => {
        register?.(id, outOfRange);
    }, [id, outOfRange, register]);
    useEffect(() => () => register?.(id, false), [id, register]);

    const handleChange = (raw: string) => {
        setText(raw);
        if (raw.trim() === "") {
            onChange(null);
            return;
        }
        const parsed = parseFloat(raw);
        if (
            Number.isFinite(parsed) &&
            parsed >= MIN_TEMPERATURE &&
            parsed <= MAX_TEMPERATURE
        ) {
            onChange(parsed);
        }
        // Out of range: keep the last committed value and surface the error.
    };

    return (
        <AcxMainTextField
            id={id}
            type="number"
            // Keep blank/inherit rendering -- without this AcxMainTextField
            // coerces an empty numeric value to 0, hiding the placeholder.
            insertBlankIfNumber
            value={text}
            isDisabled={disabled}
            placeholderText={
                placeholder != null ? String(placeholder) : undefined
            }
            onChange={(e) => handleChange(e.target.value)}
            error={outOfRange}
            errorHelper={
                outOfRange
                    ? `Temperature must be between ${MIN_TEMPERATURE} and ${MAX_TEMPERATURE}`
                    : undefined
            }
            inputProps={{
                min: MIN_TEMPERATURE,
                max: MAX_TEMPERATURE,
                step: 0.05,
            }}
        />
    );
}
